import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Text, Button } from '@radix-ui/themes';

import BroadcastMessage from './BroadcastMessage';
import BroadcastsSkeleton from './BroadcastsSkeleton';
import { useGetBroadcastReports } from './data';
import { BroadcastsSummary } from '../campaigns/components/BroadcastsSummary';
import SectionCard from '../../../shared/SectionCard/SectionCard';

const ReportsBody = ({ isFetched, reports, linkTrackingDisabled }) => {
  const { t } = useTranslation('reports');

  if (!isFetched) {
    return <BroadcastsSkeleton />;
  }

  if (reports.length === 0) {
    return (
      <Box p="5">
        <Text size="2" color="gray">
          {t('broadcasts.noDataYet')}
        </Text>
      </Box>
    );
  }

  return reports.map(report => {
    return (
      <BroadcastMessage
        key={report.id}
        broadcast={report}
        linkTrackingDisabled={linkTrackingDisabled}
      />
    );
  });
};

const ReportsFooter = ({ isLoading, reports, totalBroadcasts, onLoadMore }) => {
  const { t } = useTranslation('reports');
  const noBroadcasts = reports && !reports.length;
  const maxBroadcasts = reports && reports.length >= totalBroadcasts;

  if (isLoading || noBroadcasts || maxBroadcasts) return null;

  return (
    <Flex justify="center" py="5">
      <Button disabled={isLoading} variant="outline" onClick={onLoadMore}>
        {t('broadcasts.loadMore')}
      </Button>
    </Flex>
  );
};

const BroadcastReports = () => {
  const [limit, setLimit] = useState(5);

  const {
    data: {
      data: { reports },
      totalBroadcasts,
      linkTrackingDisabled,
    },
    isLoading,
    isFetched,
  } = useGetBroadcastReports({ limit, page: 1 });

  const { t } = useTranslation('reports');

  const handleClick = () => {
    setLimit(limit + 5);
  };

  return (
    <Flex direction="column" gap="5" width="100%">
      <BroadcastsSummary />
      <SectionCard title={t('broadcasts.header')}>
        <ReportsBody
          isFetched={isFetched}
          reports={reports}
          linkTrackingDisabled={linkTrackingDisabled}
        />
        <ReportsFooter
          isLoading={isLoading}
          reports={reports}
          totalBroadcasts={totalBroadcasts}
          onLoadMore={handleClick}
        />
      </SectionCard>
    </Flex>
  );
};

export default BroadcastReports;
